/**
 * Invite-member modal (register F-61): email (format-validated on blur and submit) + role
 * (only the roles the inviter may grant - mirrors worker-api `POST /users/invite`, where a
 * firm_admin cannot mint a managing_partner/firm_admin) + "Send invite". On success the parent
 * is told the invitation and the modal closes; a server refusal (409 existing user, 403, seats)
 * renders inline and keeps the modal open.
 */
import type { Role } from '@argus/permissions-catalogue';
import { useMutation } from '@tanstack/react-query';
import { useState, type FormEvent, type JSX } from 'react';

import { api, getErrorMessage, type FirmInvitation } from '../../lib/api.ts';
import { formatRole } from '../../lib/format/role.ts';
import { Button, Input, Modal, Notice, Select } from '../ui/index.ts';

const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Returns an error message, or null when the address is acceptable. */
export function validateInviteEmail(value: string): string | null {
  const v = value.trim();
  if (!v) return 'Enter an email address.';
  if (!EMAIL_RE.test(v)) return 'Enter a valid email address, like name@firm.com.';
  return null;
}

/** The roles an inviter may grant (client invitations go through the client portal). */
export function grantableRoles(inviterRole: Role | undefined): Role[] {
  const low: Role[] = ['senior_associate', 'associate', 'paralegal'];
  return inviterRole === 'managing_partner' ? ['managing_partner', 'firm_admin', ...low] : low;
}

export interface InviteMemberModalProps {
  inviterRole: Role | undefined;
  onClose: () => void;
  onInvited: (invitation: FirmInvitation) => void;
}

export function InviteMemberModal({
  inviterRole,
  onClose,
  onInvited,
}: InviteMemberModalProps): JSX.Element {
  const roles = grantableRoles(inviterRole);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('associate');
  const [emailError, setEmailError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => api.users.invite({ email: email.trim().toLowerCase(), role }),
    onSuccess: (inv) => onInvited(inv),
  });

  function submit(e: FormEvent): void {
    e.preventDefault();
    const err = validateInviteEmail(email);
    setEmailError(err);
    if (err) return;
    mutation.mutate();
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Invite member"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" form="invite-member-form" loading={mutation.isPending}>
            Send invite
          </Button>
        </>
      }
    >
      <form id="invite-member-form" onSubmit={submit} noValidate>
        {mutation.isError ? <Notice tone="danger">{getErrorMessage(mutation.error)}</Notice> : null}
        <Input
          label="Email"
          type="email"
          required
          autoComplete="off"
          value={email}
          error={emailError ?? undefined}
          help="They will receive a link to join this firm."
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          onBlur={() => setEmailError(email ? validateInviteEmail(email) : null)}
        />
        <Select label="Role" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          {roles.map((r) => (
            <option key={r} value={r}>
              {formatRole(r)}
            </option>
          ))}
        </Select>
      </form>
    </Modal>
  );
}
